import { useCallback, useEffect, useLayoutEffect, useState } from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "themeMode";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";
const LIGHT_VARS: Record<string, string> = {
  "--color-bg": "255 255 255",
  "--color-surface": "255 255 255",
  "--color-fg": "15 23 42",
  "--color-border": "229 231 235",
  "--color-accent": "59 130 246",
  "--color-muted": "100 116 139",
  "--bg-primary": "#ffffff",
  "--bg-secondary": "#f8fafc",
  "--font-color-primary": "#0f172a",
  "--font-color-secondary": "#64748b",
  "--playground-bg-primary": "#e2e8f0",
  "--playground-bg-secondary": "#e2e8f0",
};

const DARK_VARS: Record<string, string> = {
  "--color-bg": "15 23 42",
  "--color-surface": "30 41 59",
  "--color-fg": "241 245 249",
  "--color-border": "51 65 85",
  "--color-accent": "96 165 250",
  "--color-muted": "148 163 184",
  "--bg-primary": "#0b1220",
  "--bg-secondary": "#111827",
  "--font-color-primary": "#e2e8f0",
  "--font-color-secondary": "#94a3b8",
  "--playground-bg-primary": "#1f2937",
  "--playground-bg-secondary": "#1f2937",
};

function applyVars(vars: Record<string, string>) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

function getStoredMode(): ThemeMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // Storage can be unavailable in restricted browser sessions.
  }
  return null;
}

function getSystemMode(): ThemeMode {
  return window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";
}

function applyModeToDom(mode: ThemeMode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
  document.documentElement.classList.toggle("light", mode === "light");
  document.documentElement.style.colorScheme = mode;
  applyVars(mode === "dark" ? DARK_VARS : LIGHT_VARS);
}

export function useThemeMode(initialMode?: ThemeMode) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    return initialMode || getStoredMode() || getSystemMode();
  });

  useLayoutEffect(() => {
    applyModeToDom(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // The theme still works when the browser blocks persistence.
    }
  }, [mode]);

  useEffect(() => {
    const media = window.matchMedia(MEDIA_QUERY);
    const listener = (event: MediaQueryListEvent) => {
      if (getStoredMode()) return; // honor user override
      setModeState(event.matches ? "dark" : "light");
    };
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);

  return { mode, setMode };
}
